'use client';

import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import type { UxpHostInfo } from '@frameio/graphql-client/generated';
import { isUXPEnvironment } from '@frameio/utils/config';
import { useGetUxpHostInfoQuery } from '../gql/queries/generated/GetUxpHostInfo';

export { isVersionGreaterOrEqual } from '../utils/isVersionGreaterOrEqual';

/**
 * Context provider that manages the state of the currently active UXP Host Info
 *
 * @param children - Child components that will have access to the context
 */
export const UxpHostInfoProvider: FC<PropsWithChildren> = ({ children }) => {
  const uxpHostInfo = useGetUxpHostInfoQuery({
    context: { resolveWithUXP: true },
    skip: !isUXPEnvironment(),
  })?.data?.uxpHostInfo;

  const value: UxpHostInfo = useMemo(() => {
    const merged = { ...uxpHostInfoContextDefaults, ...uxpHostInfo };
    return {
      ...merged,
      name: merged.name === 'premierepro' ? 'premiere' : merged.name,
    };
  }, [uxpHostInfo]);

  return (
    <UxpHostInfoContext.Provider value={value}>
      {children}
    </UxpHostInfoContext.Provider>
  );
};

const uxpHostInfoContextDefaults: UxpHostInfo = {
  __typename: 'UXPHostInfo',
  /* Host application name (eg, premiere) */
  name: '',
  /* Version of the UXP extension (eg, 0.0.15) (eg, frameio-uxp plugin version from dva-js) */
  pluginVersion: '',
  /*  Version of the uxp-messaging schema (eg, 0.0.88) */
  schemaVersion: '',
  /* Locale provided by the UXP host environment (eg, en-US) */
  uiLocale: '',
  /* Version of the UXP runtime (eg, uxp-8.1.0-local) */
  uxpVersion: '',
  /* Host application version (Premiere Pro version) (eg, 25.3.0) */
  version: '',
};

const UxpHostInfoContext = createContext(uxpHostInfoContextDefaults);

export const useUxpHostInfoContext = () => useContext(UxpHostInfoContext);
